import type { YFormatType } from "../context/ChartContext";
import { pickEnergyUnit, SOURCE_UNIT_TO_WH } from "./formatting/energy";

/** Pick the display unit for a watts (W) magnitude. */
function pickPowerUnit(absW: number): "W" | "kW" | "MW" | "GW" {
  if (absW >= 1_000_000_000) return "GW";
  if (absW >= 1_000_000) return "MW";
  if (absW >= 1000) return "kW";
  return "W";
}

/** Pick the display unit for an amperes (A) magnitude. */
function pickCurrentUnit(absA: number): "mA" | "A" | "kA" {
  if (absA >= 1000) return "kA";
  if (absA < 1) return "mA";
  return "A";
}

/** Pick the display unit for a volts (V) magnitude. */
function pickVoltageUnit(absV: number): "mV" | "V" | "kV" {
  if (absV >= 1000) return "kV";
  if (absV < 1) return "mV";
  return "V";
}

/** Pick the display unit for an ohms (Ω) magnitude. */
function pickResistanceUnit(absOhm: number): "mΩ" | "Ω" | "kΩ" | "MΩ" {
  if (absOhm >= 1_000_000) return "MΩ";
  if (absOhm >= 1000) return "kΩ";
  if (absOhm < 1) return "mΩ";
  return "Ω";
}

/**
 * Pin an auto-scaling formatter to one unit for a whole axis, chosen from the
 * axis's max magnitude. Without this, each tick formats independently and one
 * axis mixes units (0 → "0.0 Wh" next to "2.0 kWh"). Formatters with an
 * explicit unit, and non-auto-scaling types, pass through unchanged.
 *
 * Handles all auto-scaling physical-quantity types:
 * energy, power, current, voltage, resistance.
 */
export function resolveAxisFormatter(formatter: YFormatType, maxAbsValue: number): YFormatType {
  if (typeof formatter !== "object" || formatter === null) return formatter;
  switch (formatter.type) {
    case "energy": {
      if (formatter.unit) return formatter;
      // When autoScale is explicitly false, the formatting layer renders base
      // units (Wh). Injecting a scaled unit here would mismatch the tick label
      // (e.g. "kWh") with the actual formatted value (e.g. "500 Wh").
      if (formatter.autoScale === false) return formatter;
      const toWhMultiplier = formatter.sourceUnit ? SOURCE_UNIT_TO_WH[formatter.sourceUnit] : 1;
      return { ...formatter, unit: pickEnergyUnit(maxAbsValue * toWhMultiplier) };
    }
    case "power":
      if (formatter.unit) return formatter;
      if (formatter.autoScale === false) return formatter;
      return { ...formatter, unit: pickPowerUnit(maxAbsValue) };
    case "current":
      if (formatter.unit) return formatter;
      if (formatter.autoScale === false) return formatter;
      return { ...formatter, unit: pickCurrentUnit(maxAbsValue) };
    case "voltage":
      if (formatter.unit) return formatter;
      if (formatter.autoScale === false) return formatter;
      return { ...formatter, unit: pickVoltageUnit(maxAbsValue) };
    case "resistance":
      if (formatter.unit) return formatter;
      if (formatter.autoScale === false) return formatter;
      return { ...formatter, unit: pickResistanceUnit(maxAbsValue) };
    default:
      return formatter;
  }
}
